import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import * as XLSX from 'xlsx';

const SeoReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Fetch all historical reports
  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/seo-results/reports`);
      if (res.ok) {
        const data = await res.json();
        setReports(data);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // Generate a new weekly snapshot
  const handleGenerateSnapshot = async () => {
    try {
      setGenerating(true);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/seo-results/reports/generate`, {
        method: 'POST'
      });
      if (res.ok) {
         await fetchReports(); // Reload the table
      } else {
        const err = await res.json();
        alert(`Failed to generate: ${err.error}`);
      }
    } catch (error) {
      console.error('Error generating snapshot:', error);
      alert('Network error while generating snapshot.');
    } finally {
      setGenerating(false);
    }
  };

  // Export a specific report's summary to Excel
  const exportReportToExcel = (report) => {
    try {
      // The summaryData comes back as an object map from the Node.js API
      const parsedSummary = typeof report.summaryData === 'string' ? JSON.parse(report.summaryData) : report.summaryData;
      
      const exportData = [];
      Object.entries(parsedSummary).forEach(([location, locData]) => {
        Object.entries(locData.brands).forEach(([brand, brandData]) => {
          exportData.push({
            'Report Week': new Date(report.reportDate).toLocaleDateString(),
            'Location': location,
            'Brand': brand,
            'Pass Count': brandData.pass,
            'Fail Count': brandData.fail,
            'Total Checks': brandData.pass + brandData.fail,
            'Pass Rate (%)': (((brandData.pass / (brandData.pass + brandData.fail)) * 100) || 0).toFixed(1)
          });
        });
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Weekly Snapshot Details");
      XLSX.writeFile(workbook, `SEO_Weekly_Report_${report.id}.xlsx`);
      
    } catch(e) {
      console.error("Failed to parse report data for export", e);
      alert("Could not export this report. Data may be corrupted.");
    }
  };

  // Format data for Recharts (reverse to show chronological order left-to-right)
  const chartData = [...reports].reverse().map(r => ({
    name: new Date(r.reportDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    PassRate: parseFloat(r.passRate),
    TotalPassed: r.totalPassed,
    TotalFailed: r.totalFailed
  }));

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-8 animate-fade-in">
      <div className="flex justify-between items-center bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-700 to-indigo-500">
            SEO Weekly Reports
          </h1>
          <p className="text-gray-500 mt-2 text-sm font-medium">Historical trends and weekly aggregate snapshots</p>
        </div>
        <div>
          <button 
            onClick={fetchReports}
            className="mr-3 px-4 py-2.5 bg-gray-50 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors shadow-sm border border-gray-200"
          >
            Refresh
          </button>
          <button 
            onClick={handleGenerateSnapshot}
            disabled={generating}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50"
          >
            {generating ? (
              <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
            )}
            {generating ? 'Capturing Snapshot...' : 'Generate Weekly Snapshot'}
          </button>
        </div>
      </div>


      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-500 font-medium">Loading reports...</p>
        </div>
      ) : reports.length > 0 ? (
        <>
          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pass Rate Trend */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-gray-800 mb-6">Pass Rate Trend (%)</h3>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#6B7280', fontSize: 12}} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#6B7280', fontSize: 12}} domain={[0, 100]} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                      formatter={(value) => [`${value}%`, 'Pass Rate']}
                    />
                    <Line type=" მონოტონური" dataKey="PassRate" stroke="#4F46E5" strokeWidth={3} dot={{r: 4, strokeWidth: 2}} activeDot={{r: 6}} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Volume Breakdown */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-gray-800 mb-6">Execution Volume</h3>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#6B7280', fontSize: 12}} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#6B7280', fontSize: 12}} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                      cursor={{fill: '#F3F4F6'}}
                    />
                    <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
                    <Bar dataKey="TotalPassed" name="Passed" stackId="a" fill="#10B981" radius={[0, 0, 4, 4]} />
                    <Bar dataKey="TotalFailed" name="Failed" stackId="a" fill="#EF4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Historical Snapshots Table */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100 bg-gray-50/50">
              <h2 className="text-lg font-bold text-gray-800">Historical Report Snapshots</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="px-6 py-4 font-semibold border-b">Report Generated</th>
                    <th className="px-6 py-4 font-semibold border-b">Data Period</th>
                    <th className="px-6 py-4 font-semibold border-b">Total Executions</th>
                    <th className="px-6 py-4 font-semibold border-b">Overall Pass Rate</th>
                    <th className="px-6 py-4 font-semibold border-b text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50/80 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-800">
                        {new Date(report.reportDate).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                        <div className="text-xs text-gray-400 font-normal mt-1">
                          {new Date(report.reportDate).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(report.startDate).toLocaleDateString()} - {new Date(report.endDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <span className="font-semibold text-gray-800">{report.totalChecked}</span>
                        <span className="text-xs ml-2 text-green-600">({report.totalPassed} Pass)</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${report.passRate >= 80 ? 'bg-green-500' : (report.passRate >= 50 ? 'bg-yellow-500' : 'bg-red-500')}`}
                              style={{ width: `${report.passRate}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-semibold text-gray-700">{report.passRate}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => exportReportToExcel(report)}
                          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center gap-1 justify-end ml-auto group"
                        >
                          Export Data
                          <svg className="w-4 h-4 opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-16 text-center">
          <div className="mx-auto w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">No Reports Generated Yet</h2>
          <p className="text-gray-500 max-w-md mx-auto mb-6">Create your first weekly snapshot by clicking the generate button above. This will analyze the last 7 days of SEO executions and freeze them into a historical report.</p>
        </div>
      )}
    </div>
  );
};

export default SeoReports;
